import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout/Layout';
import AdminChrome from './components/common/Layout/AdminChrome';
import BrandedPageLoader from './components/common/BrandedPageLoader';
import Login from './components/auth/Login';
import VerifyEmail from './components/auth/VerifyEmail';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import Users from './components/users/Users';
import Companies from './components/companies/Companies';
import CompanyDetails from './components/companies/CompanyDetails';
import CompanyVerification from './components/companies/CompanyVerification';
import Meetings from './components/meetings/Meetings';
import VideoSettings from './components/video/VideoSettings';
import Analytics from './components/analytics/Analytics';
import Sponsorships from './components/sponsorship/Sponsorships';
import Settings from './components/settings/Settings';
import Notifications from './components/notifications/Notifications';
import Profile from './components/profile/Profile';
import AdminSupport from './components/admin/AdminSupport';
import AdsReview from './components/admin/AdsReview';
import Matchmaking from './components/matchmaking/Matchmaking';
import PlatformThemeProvider from './components/common/PlatformThemeProvider';
import { BrandingProvider } from './contexts/BrandingContext';
import Employees from './components/employees/Employees';
import CRM from './components/crm/CRM';
import EmployeeMeetings from './components/employeeMeetings/EmployeeMeetings';
import EmployeeMeetingRoom from './components/employeeMeetings/EmployeeMeetingRoom';
import SubscriptionPlans from './components/subscriptions/SubscriptionPlans';
import IncompleteRegistrations from './components/registrations/IncompleteRegistrations';
import ManualPayments from './components/payments/ManualPayments';
import AuditLog from './components/audit/AuditLog';
import './App.css';
import './styles/admin-company-parity.css';

// SponsorDetails and SponsorAnalytics - create placeholder if they don't exist
import SponsorDetails from './components/sponsorship/SponsorDetails';
import SponsorAnalytics from './components/sponsorship/SponsorAnalytics';

const PUBLIC_ROUTE_PREFIXES = [
    '/login',
    '/verify-email',
    '/reset-password',
    '/forgot-password',
    '/employee-room/',
];

function AppRoutes() {
    const { user, loading, isAdmin } = useAuth();
    const location = useLocation();
    const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((path) =>
        path.endsWith('/') ? location.pathname.startsWith(path) : location.pathname === path
    );
    const showAdminChrome = !loading && !!user && isAdmin && !isPublicRoute;

    // On a hard refresh the auth profile still has to be resolved once. Use the
    // branded canvas instead of returning null/white. This branch is not used
    // for normal internal navigation, so the persistent chrome never flashes.
    if (loading && !isPublicRoute) {
        return (
            <div className="tf-admin-bootstrap-loader">
                <BrandedPageLoader label="Loading admin workspace…" />
            </div>
        );
    }

    return (
        <div className={showAdminChrome ? 'tf-admin-shell' : 'tf-public-root'}>
            {showAdminChrome && <AdminChrome />}
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reset-password" element={<ForgotPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/employee-room/:meetingId" element={<EmployeeMeetingRoom />} />

                {/* Protected Routes */}
                <Route element={<ProtectedRoute requireAdmin />}>
                    <Route element={<Layout />}>
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                        <Route path="/dashboard" element={<Dashboard />} />

                        {/* Internal Workforce */}
                        <Route path="/employees" element={<Employees />} />
                        <Route path="/crm" element={<CRM />} />
                        <Route path="/employee-meetings" element={<EmployeeMeetings />} />
                        <Route path="/subscription-plans" element={<SubscriptionPlans />} />
                        <Route path="/registrations/incomplete" element={<IncompleteRegistrations />} />
                        <Route path="/payments/manual" element={<ManualPayments />} />
                        <Route path="/audit-log" element={<AuditLog />} />

                        {/* User Management */}
                        <Route path="/users" element={<Users />} />
                        <Route path="/users/:id" element={<Users />} />

                        {/* Company Management */}
                        <Route path="/companies" element={<Companies />} />
                        <Route path="/companies/:id" element={<CompanyDetails />} />
                        <Route path="/companies/verify/:id" element={<CompanyVerification />} />

                        {/* Meeting Management */}
                        <Route path="/meetings" element={<Meetings />} />
                        <Route path="/meetings/:id" element={<Meetings />} />
                        <Route path="/meetings/calendar" element={<Meetings />} />

                        <Route path="/matchmaking" element={<Matchmaking />} />
                        <Route path="/video" element={<VideoSettings />} />
                        <Route path="/video-settings" element={<VideoSettings />} />

                        {/* Analytics */}
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/analytics/users" element={<Analytics />} />
                        <Route path="/analytics/meetings" element={<Analytics />} />
                        <Route path="/analytics/revenue" element={<Analytics />} />

                        <Route path="/ads" element={<AdsReview />} />

                        {/* Sponsorships */}
                        <Route path="/sponsorships" element={<Sponsorships />} />
                        <Route path="/sponsorships/:id" element={<SponsorDetails />} />
                        <Route path="/sponsorships/:id/analytics" element={<SponsorAnalytics />} />

                        {/* Settings */}
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/settings/general" element={<Settings />} />
                        <Route path="/settings/security" element={<Settings />} />
                        <Route path="/settings/email" element={<Settings />} />
                        <Route path="/settings/payment" element={<Settings />} />
                        <Route path="/settings/notifications" element={<Settings />} />
                        <Route path="/settings/integrations" element={<Settings />} />
                        <Route path="/settings/backup" element={<Settings />} />

                        <Route path="/admin/support" element={<AdminSupport />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/profile" element={<Profile />} />
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
        </div>
    );
}

function App() {
    return (
        <BrandingProvider>
            <PlatformThemeProvider>
                <Router>
                    <AuthProvider>
                        <Toaster
                            position="top-right"
                            toastOptions={{
                                duration: 4000,
                                style: {
                                    background: '#1a1a1a',
                                    color: '#fff',
                                    borderRadius: '10px',
                                },
                                success: {
                                    iconTheme: {
                                        primary: '#4caf50',
                                        secondary: '#fff',
                                    },
                                },
                                error: {
                                    iconTheme: {
                                        primary: '#f44336',
                                        secondary: '#fff',
                                    },
                                },
                            }}
                        />
                        <AppRoutes />
                    </AuthProvider>
                </Router>
            </PlatformThemeProvider>
        </BrandingProvider>
    );
}

export default App;